import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSound } from '../../utils/sound/SoundProvider';
import useGameBase from '../../hooks/useGameBase';

function Dice({ value = 0, isHeld = false, onClick, animate = false, index = 0 }) {
  const size = 48;
  const radius = 4.5;
  const pad = 10;
  const [displayedValue, setDisplayedValue] = useState(value);
  const coords = {
    tl: [pad, pad],
    tr: [size - pad, pad],
    ml: [pad, size / 2],
    mr: [size - pad, size / 2],
    bl: [pad, size - pad],
    br: [size - pad, size - pad],
    c: [size / 2, size / 2]
  };
  const patterns = {
    0: [],
    1: ['c'],
    2: ['tl', 'br'],
    3: ['tl', 'c', 'br'],
    4: ['tl', 'tr', 'bl', 'br'],
    5: ['tl', 'tr', 'c', 'bl', 'br'],
    6: ['tl', 'tr', 'ml', 'mr', 'bl', 'br']
  };
  const pips = patterns[displayedValue] || [];

  const bgFill = isHeld ? '#fff7ed' : '#ffffff';
  const strokeColor = isHeld ? '#d97706' : '#e5e7eb';
  const pipFill = isHeld ? '#78350f' : '#111827';

  // animate: while true cycle a few random values to simulate rolling
  const durationRef = useRef(null);
  const startDelayRef = useRef(null);
  useEffect(() => {
    const timers = [];
    if (animate && !isHeld) {
      // pick a per-die duration for this animation (persist for this roll cycle)
      if (!durationRef.current) {
        durationRef.current = 400 + Math.floor(Math.random() * 700); // 400ms..1100ms
      }
      // pick a small stagger based on index + randomness
      if (startDelayRef.current === null) {
        const base = (Number(index) || 0) * 60; // 60ms per die index
        const jitter = Math.floor(Math.random() * 80); // 0..79ms
        startDelayRef.current = base + jitter;
      }
      const totalMs = durationRef.current;
      const startDelay = startDelayRef.current || 0;
      // schedule the decelerating sequence to start after startDelay
      const starter = setTimeout(() => {
        let t = 0;
        let stepDelay = 30;
        while (t < totalMs) {
          const delay = Math.min(stepDelay, totalMs - t);
          t += delay;
          const timer = setTimeout(() => setDisplayedValue(1 + Math.floor(Math.random() * 6)), t);
          timers.push(timer);
          // increase delay to simulate slowing down
          stepDelay = Math.round(stepDelay * 1.2);
        }
        // final snap to authoritative value shortly after sequence completes
        timers.push(setTimeout(() => setDisplayedValue(value), totalMs + 40));
      }, startDelay);
      timers.push(starter);
    } else {
      // stop animation and show the authoritative value immediately
      setDisplayedValue(value);
      // reset duration so next roll picks a new randomized length
      durationRef.current = null;
      startDelayRef.current = null;
    }
    return () => { for (const id of timers) clearTimeout(id); };
  }, [animate, value, isHeld, index]);

  // Inline SVG die: clickable
  return (
    <svg
  onClick={onClick}
  width={size}
  height={size}
  viewBox={`0 0 ${size} ${size}`}
  className={animate ? 'animate-roll' : undefined}
  style={{
    cursor: 'pointer',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    // apply a subtle spin/tilt animation while animating and not held
    animationName: (animate && !isHeld) ? 'dieSpin' : undefined,
    animationDuration: (durationRef.current ? `${Math.max(400, durationRef.current)}ms` : undefined),
    animationTimingFunction: 'ease-in-out',
    animationDelay: (startDelayRef.current ? `${startDelayRef.current}ms` : undefined),
    animationFillMode: 'forwards'
  }}
      role="button"
      aria-label={displayedValue ? `Die ${displayedValue}` : 'Empty die'}
    >
      <g>
        <rect x={0.5} y={0.5} width={size - 1} height={size - 1} rx={8} ry={8} fill={bgFill} stroke={strokeColor} strokeWidth={1.5} />
        {displayedValue === 0 ? (
          <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" fontSize="14" fill="#6b7280">-</text>
        ) : (
          pips.map((k, i) => {
            const [x, y] = coords[k];
            return <circle key={i} cx={x} cy={y} r={radius} fill={pipFill} />;
          })
        )}

        {isHeld && (
          <rect x={1} y={1} width={size - 2} height={size - 2} rx={8} ry={8} fill="none" stroke="#f59e0b" strokeWidth={2} />
        )}

        {animate && (
          // simple one-shot shake animation on mount
          <animateTransform attributeName="transform" attributeType="XML" type="translate" values="0 -6 0 6 0 -3 0 0" dur="700ms" repeatCount="1" />
        )}
      </g>
    </svg>
  );
}

export default function RollFiveGame({ gameState = {}, gamePlayers = [], user = {}, onGameAction, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const localPid = user?.socketId || user?.userId || user?.id;
  const idCandidates = useMemo(() => [user?.socketId, user?.userId, user?.id].filter(Boolean), [user?.socketId, user?.userId, user?.id]);
  const idCandidatesKey = useMemo(() => idCandidates.join('|'), [idCandidates]);
  const highScoreAnnouncements = useMemo(
    () => (Array.isArray(gameState?.highScoreAnnouncements) ? gameState.highScoreAnnouncements : []),
    [gameState?.highScoreAnnouncements]
  );
  const popupRef = useRef(null);
  const [held, setHeld] = useState(new Set());
  // allow multiple open scorecards: map of pid -> true
  const [showScoreMap, setShowScoreMap] = useState(() => ({}));
  const [rollingMap, setRollingMap] = useState({}); // pid -> true when animating
  const [scoringPendingMap, setScoringPendingMap] = useState({}); // pid -> true when we've submitted a score and are waiting for server
  const sound = useSound();

  useEffect(() => {
    // find the rolls count using any id the client might have for this user
    let myRolls = 0;
    const rollsObj = gameState.perPlayerRolls || {};
    for (const c of idCandidates) {
      if (rollsObj[c] !== undefined) { myRolls = rollsObj[c]; break; }
    }
    if (myRolls === 0) setHeld(new Set());
  }, [gameState.perPlayerRolls, idCandidatesKey, idCandidates]);

  // auto-open the scorecard for the local player when they've used their final roll
  useEffect(() => {
    const rollsObj = gameState.perPlayerRolls || {};
    let myPid = null;
    for (const c of idCandidates) { if (rollsObj[c] !== undefined) { myPid = c; break; } }
    if (!myPid) myPid = idCandidates[0] || localPid;
    const myRolls = rollsObj[myPid] || 0;
    if (myRolls >= 3) {
      // only open for the local player
      if (idCandidates.includes(myPid) || myPid === localPid) setShowScoreMap(prev => ({ ...prev, [myPid]: true }));
    }
  }, [gameState.perPlayerRolls, idCandidatesKey, idCandidates, localPid]);

  // Note: clicking away will no longer close open scorecards. Scorecards are closed
  // explicitly via the Show/Hide button or after scoring. This preserves multiple
  // open scorecards while the user interacts with other parts of the UI.

  const handleToggleHold = (idx) => {
    if (!localPid) return;
  const myRolls = (gameState.perPlayerRolls && gameState.perPlayerRolls[localPid]) || 0;
  // prevent changing holds while we've submitted a score and awaiting server turn advance
  if (scoringPendingMap[localPid]) return;
    if (myRolls === 0) return;
    const next = new Set(held);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setHeld(next);
  };

  const doRoll = (pid) => {
  // block roll attempts if we've just submitted a score and are awaiting server update
  if (scoringPendingMap[pid]) return;
    const payload = { type: 'roll' };
    // include hold indices only when the roll is for the local player (match any candidate id)
    if (idCandidates.includes(pid) || pid === localPid) payload.hold = Array.from(held);
  submitAction && submitAction(payload).catch(()=>{});
  // show local rolling animation for ~700ms
  setRollingMap(prev => ({ ...prev, [pid]: true }));
  setTimeout(() => setRollingMap(prev => { const n = { ...prev }; delete n[pid]; return n; }), 700);
  // play roll sound for local player's action
  try {
    if (sound && sound.enabled && sound.enabled()) sound.play('ui:rolldie');
  } catch (e) {}
  };

  const doScore = (pid, category) => {
    // debug: log attempts to score so we can see if handler runs
    try { console.debug && console.debug('RollFive doScore', { pid, category, hasHandler: !!onGameAction }); } catch (err) {}
    // mark local scoring pending so UI blocks further actions until server advances the turn
    try { setScoringPendingMap(prev => ({ ...prev, [pid]: true })); } catch (e) {}
    if (submitAction) {
      submitAction({ type: 'score', category }).catch(()=>{});
    }
  // close the popup for this player locally
  setShowScoreMap(prev => { const n = { ...prev }; delete n[pid]; return n; });
  };

  // Clear scoring-pending flags when the current player changes (turn moved on server)
  useEffect(() => {
    const current = gameState.currentPlayer || gameState.currentPlayerSocketId || null;
    setScoringPendingMap(prev => {
      // remove any pending entries for pids that are no longer current
      const n = { ...prev };
      for (const k of Object.keys(prev)) {
        if (k !== current) delete n[k];
      }
      return n;
    });
  }, [gameState.currentPlayer, gameState.currentPlayerSocketId]);

  // When a new round starts, clear any scoring-pending flags so players can roll again
  useEffect(() => {
    try {
      setScoringPendingMap({});
    } catch (e) {}
  }, [gameState.currentRound]);

  const perPad = gameState.scorepads || {};
  const perDice = gameState.perPlayerDice || {};
  const perRolls = gameState.perPlayerRolls || {};
  const perFinished = gameState.perPlayerFinished || {};
  const perPlayerGrandRollBonus = useMemo(
    () => (gameState?.perPlayerGrandRollBonus || {}),
    [gameState?.perPlayerGrandRollBonus]
  );

  
  const DISPLAY_NAMES = {
    ones: 'Ones',
    twos: 'Twos',
    threes: 'Threes',
    fours: 'Fours',
    fives: 'Fives',
    sixes: 'Sixes',
    triple: 'Triple',
    quad: 'Quad',
    fullset: 'Full Set',
    shortchain: 'Short Chain',
    longchain: 'Long Chain',
    grandroll: 'Grand Roll',
    wild: 'Wild'
  };

  const scoreForCategory = (dice, cat) => {
    const c = {};
    // robustly count numeric die faces (accept numbers or numeric strings)
    for (const v of dice) {
      const n = Number(v) || 0;
      if (n > 0) c[n] = (c[n] || 0) + 1;
    }
    const sum = (arr) => arr.reduce((a,b)=>a+(b||0),0);
    const hasSequence = (arr, length) => {
      const s = new Set(arr.filter(Boolean));
      for (let start=1; start<=6-length+1; start++) {
        let ok = true;
        for (let k=0;k<length;k++) if (!s.has(start+k)) { ok=false; break; }
        if (ok) return true;
      }
      return false;
    };
    switch(cat) {
      case 'ones': return (c[1]||0)*1;
      case 'twos': return (c[2]||0)*2;
      case 'threes': return (c[3]||0)*3;
      case 'fours': return (c[4]||0)*4;
      case 'fives': return (c[5]||0)*5;
      case 'sixes': return (c[6]||0)*6;
      case 'triple': return Object.values(c).some(x=>x>=3) ? sum(dice) : 0;
      case 'quad': return Object.values(c).some(x=>x>=4) ? sum(dice) : 0;
      case 'fullset': {
        const isFull = (Object.values(c).some(x=>x===3) && Object.values(c).some(x=>x===2));
        try { console.debug && console.debug('RollFive fullset check', { dice, counts: c, isFull }); } catch (err) {}
        return isFull ? 25 : 0;
      }
      case 'shortchain': return hasSequence(dice,4) ? 30 : 0;
      case 'longchain': return hasSequence(dice,5) ? 40 : 0;
      case 'grandroll': return Object.values(c).some(x=>x===5) ? 50 : 0;
      case 'wild': return sum(dice);
      default: return 0;
    }
  };

  // NOTE: removed automatic clearing of rollingMap when server dice update arrives.
  // The local animation is driven by doRoll's timer so the dice will animate
  // through multiple faces before snapping to the final value.

  const playersSorted = (() => {
    const players = Array.isArray(gamePlayers) ? [...gamePlayers] : [];
    const uid = localPid;
    if (uid) {
  const idx = players.findIndex(p => p.socketId === uid || p.userId === uid || p.id === uid);
      if (idx > 0) {
        const [me] = players.splice(idx, 1);
        players.unshift(me);
      }
    }
    return players;
  })();

  const finalScores = useMemo(() => {
    const resultScores = gameState?.result?.breakdown?.scores || {};
    const stateScores = gameState?.scores || {};
    const mergedScores = { ...stateScores, ...resultScores };

    return playersSorted
      .map((player) => {
        const candidateIds = [player.socketId, player.userId, player.id].filter(Boolean);
        const playerKey = candidateIds.find((id) => Object.prototype.hasOwnProperty.call(mergedScores, id)) || candidateIds[0] || '';
        const score = Number(mergedScores[playerKey] ?? 0);
        const announcement = highScoreAnnouncements.find((entry) => {
          const entryPlayerId = String(entry?.playerId || '');
          return candidateIds.some((id) => String(id) === entryPlayerId);
        }) || null;

        return {
          key: playerKey || player.username || 'player',
          username: player.username || player.name || player.displayName || playerKey || 'Player',
          score,
          grandRollBonus: Number(mergedScores[playerKey] != null ? (perPlayerGrandRollBonus[playerKey] || 0) : 0),
          announcement,
          isLocalPlayer: candidateIds.some((id) => idCandidates.includes(id)),
        };
      })
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.username.localeCompare(right.username);
      });
  }, [gameState?.result?.breakdown?.scores, gameState?.scores, highScoreAnnouncements, idCandidates, perPlayerGrandRollBonus, playersSorted]);

  const winnerIds = Array.isArray(gameState?.result?.winners) ? gameState.result.winners.map(String) : [];

  
  // alwaysShowMap removed — scorecards are only shown/hidden explicitly per-player

  return (
    <>
    <div className="mt-4 text-sm text-gray-600 dark:text-gray-300">Round {gameState.currentRound} / {gameState.totalRounds}</div>
    <div className="w-full text-gray-800 dark:text-gray-100">
      <style>{`
        @keyframes rollShake { 0% { transform: translateY(0) rotate(0deg);} 20% { transform: translateY(-6px) rotate(-8deg);} 40% { transform: translateY(6px) rotate(6deg);} 60% { transform: translateY(-3px) rotate(-4deg);} 100% { transform: translateY(0) rotate(0deg);} }
        .animate-roll { animation: rollShake 700ms ease-in-out; }
        @keyframes pipFlicker { 0%{opacity:1} 50%{opacity:0.2} 100%{opacity:1} }
        .animate-roll circle { animation: pipFlicker 700ms linear; }
  @keyframes dieSpin { 0% { transform: translateY(0) rotate(0deg) scale(1);} 30% { transform: translateY(-8px) rotate(-18deg) scale(1.02);} 60% { transform: translateY(6px) rotate(12deg) scale(0.98);} 100% { transform: translateY(0) rotate(0deg) scale(1);} }
      `}</style>

      

      {gameState.finished && finalScores.length > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/80 p-4 shadow-sm dark:border-amber-900/60 dark:bg-amber-950/30">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-700 dark:text-amber-300">Final Scores</div>
              <div className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {winnerIds.length > 1 ? 'Tied finish' : 'Game complete'}
              </div>
            </div>
            <div className="text-sm text-slate-600 dark:text-slate-300">
              {winnerIds.length > 1 ? `${winnerIds.length} players tied for first` : 'Highest score wins'}
            </div>
          </div>

          <div className="space-y-2">
            {finalScores.map((entry, index) => {
              const isWinner = playersSorted.some((player) => {
                const ids = [player.socketId, player.userId, player.id].filter(Boolean).map(String);
                return entry.username === (player.username || player.name || player.displayName || '') && ids.some((id) => winnerIds.includes(id));
              });

              return (
                <div
                  key={`${entry.key}-${index}`}
                  className={`rounded-xl border px-3 py-3 ${isWinner ? 'border-emerald-300 bg-emerald-100/70 dark:border-emerald-700 dark:bg-emerald-900/30' : 'border-slate-200 bg-white/80 dark:border-slate-700 dark:bg-slate-900/50'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900 dark:text-slate-100">
                        {index + 1}. {entry.username}{entry.isLocalPlayer ? ' (You)' : ''}{isWinner ? ' 👑' : ''}
                      </div>
                      {entry.announcement && (
                        <div className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
                          Entered the high scores at #{entry.announcement.rank} for {entry.announcement.metricLabel}.
                        </div>
                      )}
                      {entry.grandRollBonus > 0 && (
                        <div className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                          Grand Roll bonus: +{entry.grandRollBonus} ({entry.grandRollBonus / 100} extra grand roll{entry.grandRollBonus === 100 ? '' : 's'})
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{entry.score}</div>
                      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">points</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {playersSorted.map((p) => {
          const candidateIds = [p.socketId, p.userId, p.id].filter(Boolean);
          let pid = candidateIds.find(id => (perRolls[id] !== undefined) || (perDice[id] !== undefined) || (perPad[id] !== undefined) || (perFinished[id] !== undefined));
          if (!pid) pid = candidateIds[0] || '';
          const isMe = idCandidates.includes(pid) || (!!localPid && localPid === pid);
          const dice = perDice[pid] || [0,0,0,0,0];
          const rolls = perRolls[pid] || 0;
          const finished = !!perFinished[pid];
          const grandRollBonus = Number(perPlayerGrandRollBonus[pid] || 0);
          const remaining = Math.max(0, 3 - rolls);
          const rollLabel = remaining > 1 ? `Roll x${remaining}` : remaining === 1 ? 'Last Roll' : 'No Rolls';
          const scoreVisible = !!showScoreMap[pid];
          const scoringPending = !!scoringPendingMap[pid];
          const canRoll = isMe && remaining > 0 && !scoringPending;

          return (
            <div key={pid} className={`p-2 rounded ${finished ? 'opacity-60' : (isMe ? 'bg-gray-50 dark:bg-gray-700' : 'bg-white dark:bg-gray-800')}`}>
              <div className="w-full flex items-center justify-between">
                <div><div className="font-semibold truncate">{p.username || p.name || p.displayName || pid}
                    <button type="button" onClick={() => {
                      const newVisible = !scoreVisible;
                      // update transient UI state
                      setShowScoreMap(prev => {
                        const n = { ...prev };
                        if (newVisible) n[pid] = true; else delete n[pid];
                        return n;
                      });
                    }} className="ml-4 scorecard-toggle text-blue-600 dark:text-blue-400 text-sm underline">{scoreVisible ? 'Hide Scorecard' : 'Show Scorecard'}</button>
                    </div>
                    
                    </div>
                <div className="flex items-center gap-2" />
                {finished ? (
                    <div className="text-sm text-green-600 dark:text-green-400">Done</div>
                  ) : isMe ? (
                    remaining === 0 ? (
                      <div className="text-sm text-yellow-600 dark:text-yellow-400">Please score</div>
                    ) : (
                      <div className="text-sm text-gray-600 dark:text-gray-400">You have {remaining} roll{remaining !== 1 ? 's' : ''} left</div>
                    )
                  ) : (
                    <div className="text-sm text-yellow-600 dark:text-yellow-400">Playing</div>
                  )}
                  <div>
                 
                </div>
              </div>
              {grandRollBonus > 0 && (
                <div className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                  Grand Roll bonus: +{grandRollBonus}
                </div>
              )}
              

              <div className="mt-2">
                <div className="flex items-start gap-4">
                  <div className="flex gap-2">
                    {dice.map((d, i) => {
                      const isHeld = isMe && held.has(i);
                      const animateDie = !!rollingMap[pid] && !isHeld;
                      return <Dice key={i} value={d} isHeld={isHeld} animate={animateDie} onClick={() => isMe && handleToggleHold(i)} />;
                    })}
                  </div>

                  <div className="flex flex-col items-start gap-2">
                    <button onClick={() => { if (canRoll) doRoll(pid); }} disabled={!canRoll} className={`px-3 py-1 rounded ${canRoll ? 'bg-blue-600 dark:bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 opacity-50 cursor-not-allowed'}`}>
                      {rollLabel}
                    </button>
                  </div>
                </div>
              </div>

              {(!!showScoreMap[pid]) && (
                <div ref={popupRef} className="scorecard-popup mt-2 z-50" style={{ width: '320px' }}>
                  <div className="p-2 bg-white dark:bg-gray-800 rounded shadow text-sm">
                    <div className="text-xs font-semibold mb-1">Scorecard</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        {['ones','twos','threes','fours','fives','sixes'].map((cat) => {
                          const assigned = perPad[pid] && typeof perPad[pid][cat] !== 'undefined' ? perPad[pid][cat] : null;
                          const preview = scoreForCategory(perDice[pid] || [0,0,0,0,0], cat);
                          const displayValue = !isMe ? (assigned === null ? '-' : assigned) : (assigned === null ? preview : assigned);
                          const hasRolled = (perRolls && (perRolls[pid] || 0) > 0);
                          // disable if already assigned, not the local player, or the player hasn't rolled yet
                          const disabled = assigned !== null || !isMe || !hasRolled;
                          const disableAll = !!scoringPendingMap[pid];
                          // If already assigned, show static entry
                          if (assigned !== null) {
                            return (
                              <div key={cat} className="w-full px-2 py-1 rounded text-xs bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="font-semibold">{displayValue}</div>
                              </div>
                            );
                          }
                          // If we've just submitted a score this round, show static preview (no buttons)
                          if (disableAll) {
                            return (
                              <div key={cat} className="w-full px-2 py-1 rounded text-xs bg-gray-200 dark:bg-gray-700 flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="text-sm font-semibold ml-2">{preview}</div>
                              </div>
                            );
                          }
                          // Normal interactive button
                          return (
                            <button type="button" key={cat} onClick={() => !disabled && doScore(pid, cat)} disabled={disabled} className={`w-full text-left px-2 py-1 rounded text-xs ${disabled ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 cursor-not-allowed' : 'bg-indigo-600 dark:bg-indigo-500 text-white'}`}>
                              <div className="flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="text-sm font-semibold ml-2">{displayValue}</div>
                              </div>
                            </button>
                          );
                        })}

                        {/* Bonus */}
                        {(() => {
                          const upperCats = ['ones','twos','threes','fours','fives','sixes'];
                          let upperTotal = 0;
                          for (const uc of upperCats) {
                            const assigned = perPad[pid] && typeof perPad[pid][uc] !== 'undefined' ? perPad[pid][uc] : null;
                            if (assigned !== null && typeof assigned !== 'undefined') { upperTotal += assigned; }
                            else if (isMe) { upperTotal += scoreForCategory(perDice[pid] || [0,0,0,0,0], uc); }
                          }
                          const bonusAssigned = perPad[pid] && typeof perPad[pid].bonus !== 'undefined' ? perPad[pid].bonus : null;
                          const bonusValue = (bonusAssigned !== null && typeof bonusAssigned !== 'undefined') ? bonusAssigned : (upperTotal > 63 ? 35 : '-');
                          return (
                            <div className="w-full px-2 py-1 rounded text-xs bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
                              <div className="truncate">Bonus (&gt;63)</div>
                              <div className="font-semibold">{bonusValue}</div>
                            </div>
                          );
                        })()}
                      </div>

                      <div className="space-y-1">
                        {['triple','quad','fullset','shortchain','longchain','grandroll','wild'].map((cat) => {
                          const assigned = perPad[pid] && typeof perPad[pid][cat] !== 'undefined' ? perPad[pid][cat] : null;
                          const preview = scoreForCategory(perDice[pid] || [0,0,0,0,0], cat);
                          const displayValue = !isMe ? (assigned === null ? '-' : assigned) : (assigned === null ? preview : assigned);
                          const hasRolled = (perRolls && (perRolls[pid] || 0) > 0);
                          // disable if already assigned, not the local player, or the player hasn't rolled yet
                          const disabled = assigned !== null || !isMe || !hasRolled;
                          const disableAll = !!scoringPendingMap[pid];
                          if (assigned !== null) {
                            return (
                              <div key={cat} className="w-full px-2 py-1 rounded text-xs bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="font-semibold">{displayValue}</div>
                              </div>
                            );
                          }
                          if (disableAll) {
                            return (
                              <div key={cat} className="w-full px-2 py-1 rounded text-xs bg-gray-200 dark:bg-gray-700 flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="text-sm font-semibold ml-2">{preview}</div>
                              </div>
                            );
                          }
                          return (
                            <button type="button" key={cat} onClick={() => !disabled && doScore(pid, cat)} disabled={disabled} className={`w-full text-left px-2 py-1 rounded text-xs ${disabled ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-300 cursor-not-allowed' : 'bg-indigo-600 dark:bg-indigo-500 text-white'}`}>
                              <div className="flex justify-between items-center">
                                <div className="truncate">{DISPLAY_NAMES[cat] || cat}</div>
                                <div className="text-sm font-semibold ml-2">{displayValue}</div>
                              </div>
                            </button>
                          );
                        })}
                        <div className="w-full px-2 py-1 rounded text-xs bg-amber-50 dark:bg-amber-950/40 flex justify-between items-center">
                          <div className="truncate">Grand Roll Bonus</div>
                          <div className="font-semibold">{grandRollBonus > 0 ? `+${grandRollBonus}` : '-'}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-2 flex items-center justify-between">
                

                <div>
                  
                </div>
              </div>
            </div>
          );
        })}
      </div>

      
    </div>
    </>
  );
}

RollFiveGame.Guide = function RollFiveGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">How to Play Roll Five</h3>
      <ul className="list-disc ml-6">
        <li>Roll five dice and try to make the best combination.</li>
        <li>Score points based on the combination you make.</li>
        <li>Highest score at the end of the game wins!</li>
        <li>You can only apply a score once per category so choose wisely</li>
        <li>There is a bonus for getting an average of 3 of a kind in the first 6 categories</li>
      </ul>
    </div>
  );
};